// app/src/trading-journal/symbolCommissions.ts

export interface CommissionTemplate {
  commissionPerContract?: number | null;
  symbolCommissions?: Record<string, number> | null;
}

/**
 * Résout le taux de commission ($/contrat, valeur positive) pour un symbole donné.
 * Lookup par préfixe : "NQM6" matche la clé "NQ" si symbol.startsWith("NQ").
 * En cas de plusieurs préfixes matchants, le plus long est prioritaire.
 * Retourne null si aucun taux trouvé.
 */
export function resolveCommissionForSymbol(
  symbol: string,
  template: CommissionTemplate | null | undefined,
): number | null {
  if (!template) return null;

  if (template.symbolCommissions && typeof template.symbolCommissions === "object") {
    const entries = Object.entries(template.symbolCommissions as Record<string, number>);
    const match = entries
      .filter(([prefix]) => symbol.toUpperCase().startsWith(prefix.toUpperCase()))
      .sort((a, b) => b[0].length - a[0].length)[0];
    if (match !== undefined) return match[1];
  }

  return template.commissionPerContract ?? null;
}

/** Sous-ensemble commission d'un PropFirmTier (les deux colonnes homonymes). */
export interface TierCommissionSource {
  commissionPerContract?: number | null;
  symbolCommissions?: Record<string, number> | null;
}

/**
 * Fusionne les commissions de la prop firm et celles du palier actif.
 * Champ par champ : le palier ne surcharge que ce qu'il définit.
 * - taux plat : tier ?? firm
 * - table par symbole : union des préfixes (normalisés en MAJUSCULES,
 *   cohérent avec resolveCommissionForSymbol qui compare en majuscules),
 *   le tier gagne sur un préfixe présent des deux côtés.
 * Rend null si aucune source, pour que les appelants gardent leur
 * comportement actuel (aucun pré-remplissage).
 */
export function mergeCommissionSources(
  firm: CommissionTemplate | null | undefined,
  tier: TierCommissionSource | null | undefined,
): CommissionTemplate | null {
  if (!firm && !tier) return null;

  const merged: Record<string, number> = {};
  for (const source of [firm?.symbolCommissions, tier?.symbolCommissions]) {
    if (!source || typeof source !== "object") continue;
    for (const [prefix, rate] of Object.entries(source as Record<string, number>)) {
      merged[prefix.toUpperCase()] = rate;
    }
  }

  return {
    commissionPerContract: tier?.commissionPerContract ?? firm?.commissionPerContract ?? null,
    symbolCommissions: Object.keys(merged).length > 0 ? merged : null,
  };
}
